import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import VillaCard from './VillaCard';
import { 
  Heart, 
  Star, 
  Filter,
  Grid3X3,
  List,
  ArrowRight,
  Search,
  Calendar,
  Users,
  MapPin,
  X,
  Share2,
  Download
} from 'lucide-react';
import { sampleVillas } from '../data/mock';

const FavoritesPage = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState(new Set(['villa-serenite', 'villa-majeste'])); // Mock favorites
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'price', 'name'
  const [filterRegion, setFilterRegion] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Get favorite villas
  const favoriteVillas = sampleVillas.filter(villa => favorites.has(villa.id));

  // Filter and sort favorites
  const filteredFavorites = favoriteVillas
    .filter(villa => {
      const matchesSearch = villa.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          villa.location.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRegion = filterRegion === 'all' || villa.location.toLowerCase().includes(filterRegion.toLowerCase());
      return matchesSearch && matchesRegion;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price':
          return parseInt(a.priceDisplay.replace(/[^0-9]/g, '')) - parseInt(b.priceDisplay.replace(/[^0-9]/g, ''));
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0; // Keep original order for 'recent'
      }
    });

  const handleToggleFavorite = (villaId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(villaId)) {
        newFavorites.delete(villaId);
      } else {
        newFavorites.add(villaId);
      }
      return newFavorites;
    });
  };

  const handleViewDetails = (villa) => {
    navigate(`/villa/${villa.id}`);
  };

  const handleClearAll = () => {
    setFavorites(new Set());
  };

  const handleShareFavorites = () => {
    // Mock share functionality
    console.log('Sharing favorites...');
  };

  const handleExportFavorites = () => {
    // Mock export functionality
    console.log('Exporting favorites...');
  };

  if (favoriteVillas.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            {/* Empty State */}
            <div className="mb-12">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="w-12 h-12 text-gray-400" />
              </div>
              <h1 
                className="text-4xl font-light mb-4 text-gray-800"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Votre Collection de Rêves
              </h1>
              <p 
                className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Créez votre sélection personnelle des plus belles villas PRIVIA. 
                Chaque favori vous rapproche de votre prochaine évasion de luxe.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-3 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
              >
                Découvrir nos Villas
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Sauvegardez vos Coups de Cœur</h3>
                <p className="text-gray-600 text-sm">Marquez d'un clic les villas qui vous inspirent</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Share2 className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Partagez vos Sélections</h3>
                <p className="text-gray-600 text-sm">Envoyez votre wishlist à vos proches</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">Réservez plus Facilement</h3>
                <p className="text-gray-600 text-sm">Accès direct à vos villas préférées</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <section className="py-16 px-6 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="flex-1">
              <Badge 
                variant="secondary" 
                className="mb-4 px-4 py-2 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
              >
                <Heart className="w-4 h-4 mr-2" />
                Mes Favoris
              </Badge>
              
              <h1 
                className="text-4xl md:text-5xl font-light mb-4 tracking-wide"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Ma Collection Privée
              </h1>
              
              <p 
                className="text-xl font-light opacity-90 max-w-2xl leading-relaxed"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {favoriteVillas.length} villa{favoriteVillas.length > 1 ? 's' : ''} d'exception sélectionnée{favoriteVillas.length > 1 ? 's' : ''} avec soin
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleShareFavorites}
                className="border-white/30 text-white hover:bg-white/10 backdrop-blur-sm"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Partager
              </Button>
              <Button
                variant="outline"
                onClick={handleExportFavorites}
                className="border-white/30 text-white hover:bg-white/10 backdrop-blur-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Filters and Controls */}
      <section className="py-8 px-6 bg-white/50 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Rechercher dans mes favoris..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white/80 backdrop-blur-sm"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
              <select
                value={filterRegion}
                onChange={(e) => setFilterRegion(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white/80"
              >
                <option value="all">Toutes les régions</option>
                <option value="caraïbes">Caraïbes</option>
                <option value="france">France</option>
                <option value="italie">Italie</option>
                <option value="grèce">Grèce</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white/80"
              >
                <option value="recent">Plus récents</option>
                <option value="name">Nom A-Z</option>
                <option value="price">Prix croissant</option>
              </select>

              {/* View Toggle */}
              <div className="flex border border-gray-200 rounded-lg overflow-hidden bg-white/80">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-amber-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-amber-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              {favoriteVillas.length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleClearAll}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Tout supprimer
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {filteredFavorites.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-medium text-gray-800 mb-2">Aucun résultat</h3>
              <p className="text-gray-600">Essayez de modifier vos critères de recherche</p>
            </div>
          ) : (
            <>
              {/* Results Header */}
              <div className="flex items-center justify-between mb-8">
                <p className="text-gray-600">
                  {filteredFavorites.length} villa{filteredFavorites.length > 1 ? 's' : ''} trouvée{filteredFavorites.length > 1 ? 's' : ''}
                </p>
              </div>

              {/* Grid View */}
              {viewMode === 'grid' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredFavorites.map((villa) => (
                    <VillaCard
                      key={villa.id}
                      villa={villa}
                      onViewDetails={handleViewDetails}
                      onToggleFavorite={handleToggleFavorite}
                      isFavorite={favorites.has(villa.id)}
                    />
                  ))}
                </div>
              )}

              {/* List View */}
              {viewMode === 'list' && (
                <div className="space-y-6">
                  {filteredFavorites.map((villa) => (
                    <Card key={villa.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                      <div className="flex">
                        <div className="w-1/3 h-48 relative">
                          <img
                            src={villa.mainImage}
                            alt={villa.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 
                                className="text-2xl font-light text-gray-800 mb-2"
                                style={{ fontFamily: 'Playfair Display, serif' }}
                              >
                                {villa.name}
                              </h3>
                              <div className="flex items-center gap-2 text-gray-600 mb-2">
                                <MapPin className="w-4 h-4" />
                                <span>{villa.location}</span>
                              </div>
                              <p className="text-gray-600 text-sm italic mb-4">{villa.emotionalTagline}</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-light text-amber-600 mb-1">{villa.priceDisplay}</div>
                              <div className="text-sm text-gray-500">{villa.priceSubtext}</div>
                            </div>
                          </div>

                          <div className="flex items-center gap-6 mb-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              <span>{villa.capacity}</span>
                            </div>
                            <span>•</span>
                            <span>{villa.bedrooms} chambres</span>
                            <span>•</span>
                            <span>{villa.bathrooms} salles de bain</span>
                          </div>

                          <div className="flex items-center justify-between">
                            <Button
                              onClick={() => handleViewDetails(villa)}
                              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-6 py-2 rounded-full font-medium transition-all duration-300"
                            >
                              Voir les détails
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                            
                            <Button
                              variant="ghost"
                              onClick={() => handleToggleFavorite(villa.id)}
                              className="p-2 text-red-500 hover:bg-red-50"
                            >
                              <Heart className="w-5 h-5" fill="currentColor" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default FavoritesPage;